import { useEffect, useRef, useState } from 'react';
import { BellRing, LogOut, RefreshCw, UserRound } from 'lucide-react';
import { getAuthStore, useAuth } from '../../auth/authStore.ts';
import { getSyncClient, useSync } from '../../sync/syncClient.ts';
import { useStore } from '../../store/appStore.ts';
import { hostOf } from '../../config.ts';
import { isNative } from '../../platform.ts';
import { Button, Sheet } from '../../ui/kit.tsx';
import { errorText, toast } from '../../ui/toast.tsx';
import { displayName, syncStatus } from './status.ts';
import { StatusIcon } from './AccountWidgets.tsx';
import { tr } from '../../i18n/i18n.ts';
import { formatInstant } from '../../i18n/format.ts';

/** Konto: identitet, separat synkstatus och direkt synlig Logga ut. Diagnostik ligger under Avancerat. */
export function AccountSheet({ onClose, onLogout }: { onClose: () => void; onLogout: (opts?: { deleteLocal?: boolean }) => void }) {
  const a = useAuth();
  const sync = useSync();
  const { state, device, storageKind } = useStore();
  const st = a.state;
  if (st.status === 'guest') return null;
  const p = st.profile;
  const base = getAuthStore().baseFor(p) ?? '';
  const s = syncStatus(st, sync);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} side title={tr('Konto', 'Account')}>
      <div className="flex flex-col gap-4 text-sm">
        <div>
          <p className="flex items-center gap-2 text-base font-semibold">
            <UserRound size={18} className="text-accent" aria-hidden /> {displayName(p.name, p.email)}
          </p>
          <p className="break-all text-muted">{p.email}</p>
          <p className="mt-1 text-xs text-muted">
            {tr('AutoDay-konto på ', 'AutoDay account on ')}
            {hostOf(base)}
            {base ? tr(' (egen server)', ' (own server)') : ''}. {tr('Ditt AutoDay-konto är separat från andra tjänster.', 'Your AutoDay account is separate from other services.')}
          </p>
        </div>

        <a href="#/plus" onClick={onClose} className="inline-flex min-h-11 items-center justify-between rounded-lg border border-border p-3 font-medium text-accent hover:bg-raised">
          AutoDay Plus <span>{tr('Priser och köp', 'Pricing and checkout')} →</span>
        </a>

        <section aria-labelledby="ac-sync" className="rounded-lg border border-border p-3">
          <h3 id="ac-sync" className="text-xs font-semibold uppercase tracking-wide text-muted">
            {tr('Synk', 'Sync')}
          </h3>
          <p className="mt-1 flex items-center gap-1.5" role="status" aria-live="polite">
            <StatusIcon tone={s.tone} icon={s.icon} />
            {s.label}
          </p>
          {sync.lastSyncAt && <p className="text-xs text-muted">{tr('Senast bekräftad synk:', 'Last confirmed sync:')} {formatInstant(sync.lastSyncAt, state.settings.timeZone)}</p>}
          {sync.lastError && sync.state !== 'idle' && <p className="text-xs text-risk">{sync.lastError}</p>}
          {st.status === 'signed-in' && (
            <Button className="mt-2" icon={<RefreshCw size={16} aria-hidden />} onClick={() => void getSyncClient().syncNow()} disabled={sync.state === 'syncing'}>
              {tr('Synka nu', 'Sync now')}
            </Button>
          )}
          {st.status === 'expired' && <p className="mt-1 text-risk">{tr('Sessionen har gått ut. Logga in igen för att synka. Dina ändringar finns kvar på den här enheten.', 'The session has expired. Sign in again to sync. Your changes are still on this device.')}</p>}
        </section>

        <section aria-labelledby="ac-session" className="rounded-lg border border-border p-3">
          <h3 id="ac-session" className="text-xs font-semibold uppercase tracking-wide text-muted">
            {tr('Inloggning på den här enheten', 'Sign-in on this device')}
          </h3>
          <p className="mt-1">
            {p.rememberMe
              ? tr('Ihågkommen på den här enheten: du förblir inloggad tills du loggar ut. Efter 30 dagar utan användning behöver du logga in igen.', 'Remembered on this device: you stay signed in until you sign out. After 30 days without use you need to sign in again.')
              : tr(`Inte ihågkommen: du loggas ut när ${isNative() ? 'appen avslutas' : 'webbläsarsessionen tar slut'}, senast efter 24 timmar.`, `Not remembered: you are signed out when ${isNative() ? 'the app is closed' : 'the browser session ends'}, at the latest after 24 hours.`)}
          </p>
        </section>

        <a href="#/notiser" onClick={onClose} className="inline-flex min-h-11 items-center gap-2 font-medium text-accent hover:underline">
          <BellRing size={16} aria-hidden /> {tr('Aktivera påminnelser på den här enheten', 'Turn on reminders on this device')}
        </a>
        <p className="-mt-3 text-xs text-muted">{tr('Inloggning betyder inte att påminnelser är aktiva. Behörighet och test görs i Påminnelsekoll.', 'Being signed in does not mean reminders are active. Permission and test are in Reminder check.')}</p>

        <Button variant="danger" icon={<LogOut size={16} aria-hidden />} onClick={() => onLogout()} disabled={a.switching}>
          {tr('Logga ut', 'Sign out')}
        </Button>
        <p className="-mt-2 text-xs text-muted">{tr('Loggar ut den här enheten. Dina andra enheter förblir inloggade och kontots data på servern finns kvar.', 'Signs out this device. Your other devices stay signed in and the account’s data on the server is kept.')}</p>

        <details className="rounded-lg border border-border px-3 py-2">
          <summary className="min-h-9 cursor-pointer py-1 text-muted">{tr('Avancerat', 'Advanced')}</summary>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted">{tr('Server', 'Server')}</dt>
            <dd className="break-all">{p.serverOrigin ?? hostOf(base)}</dd>
            <dt className="text-muted">{tr('Enhets-ID', 'Device ID')}</dt>
            <dd className="break-all">
              <code>{device.deviceId}</code>
            </dd>
            <dt className="text-muted">{tr('Lokal profil', 'Local profile')}</dt>
            <dd className="break-all">
              <code>{p.id}</code> ({storageKind === 'indexeddb' ? 'IndexedDB' : 'SQLite'}: {p.dbName})
            </dd>
          </dl>
          <p className="mt-2 text-xs text-muted">{tr('Den lokala kopian av kontot ligger i en egen profil på enheten, skild från gästläget. Den är inte krypterad i vila utöver vad enheten själv gör.', 'The local copy of the account is in its own profile on the device, separate from guest mode. It is not encrypted at rest beyond what the device itself does.')}</p>
          <Button className="mt-2" variant="danger" onClick={() => onLogout({ deleteLocal: true })}>
            {tr('Radera den lokala kopian och logga ut…', 'Delete the local copy and sign out…')}
          </Button>
        </details>
      </div>
    </Sheet>
  );
}

/**
 * Utloggning med förutsägbara följder. Inga osynkade ändringar → loggar ut direkt (ingen onödig ruta).
 * Annars: Synka först / Spara ändringarna lokalt och logga ut / Avbryt. Radering av den lokala kopian
 * kräver uttrycklig bekräftelse och påverkar aldrig serverns data.
 */
export function LogoutFlow({ deleteLocal: initialDelete, onDone }: { deleteLocal: boolean; onDone: () => void }) {
  const auth = getAuthStore();
  const sync = getSyncClient();
  const [phase, setPhase] = useState<'checking' | 'ask' | 'working'>('checking');
  const [unsynced, setUnsynced] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [wantDelete, setWantDelete] = useState(initialDelete);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [workingLabel, setWorkingLabel] = useState(() => tr('Loggar ut…', 'Signing out…'));
  const started = useRef(false);

  async function doLogout(deleteLocal: boolean) {
    setWorkingLabel(deleteLocal ? tr('Raderar den lokala kopian och loggar ut…', 'Deleting the local copy and signing out…') : tr('Loggar ut…', 'Signing out…'));
    setPhase('working');
    try {
      await auth.logout({ deleteLocal });
      // Sidan laddas om av AuthStore.
    } catch (e) {
      setError(tr(`Utloggningen kunde inte slutföras: ${errorText(e)}`, `The sign-out could not be completed: ${errorText(e)}`));
      setPhase('ask');
    }
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      await sync.pause();
      const n = await sync.unsyncedCount();
      setUnsynced(n);
      if (n === 0 && !initialDelete) await doLogout(false);
      else setPhase('ask');
    })();
  }, []);

  function cancel() {
    if (phase === 'working') return;
    sync.resume();
    onDone();
  }

  async function syncFirst() {
    setError(null);
    setWorkingLabel(tr('Synkar ändringarna…', 'Syncing the changes…'));
    setPhase('working');
    const r = await sync.flush();
    if (r.ok) await doLogout(false);
    else {
      setUnsynced(r.pending + r.conflicts);
      setError(tr(`Kunde inte synka: ${r.error ?? 'okänt fel'} Inget har ändrats – välj igen.`, `Could not sync: ${r.error ?? 'unknown error'} Nothing has changed – choose again.`));
      setPhase('ask');
    }
  }

  if (phase === 'checking' || (phase === 'working' && !error)) {
    return (
      <Sheet open onOpenChange={() => undefined} title={tr('Loggar ut', 'Signing out')} hideClose>
        <p className="text-sm" role="status">
          {phase === 'checking' ? tr('Kontrollerar osynkade ändringar…', 'Checking unsynced changes…') : workingLabel}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet open onOpenChange={(o) => !o && cancel()} title={wantDelete ? tr('Radera den lokala kopian av kontot', 'Delete the local copy of the account') : tr('Ändringar som inte har synkats', 'Changes that have not been synced')}>
      <div className="grid gap-3 text-sm">
        {unsynced > 0 ? (
          <p>
            {tr('Du har ', 'You have ')}
            <strong>{unsynced}</strong> {tr(`ändring${unsynced === 1 ? '' : 'ar'} som servern ännu inte har bekräftat.`, `change${unsynced === 1 ? '' : 's'} that the server has not confirmed yet.`)}
          </p>
        ) : (
          <p>{tr('Alla ändringar är synkade till kontot.', 'All changes are synced to the account.')}</p>
        )}
        {error && (
          <p className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2" role="alert">
            {error}
          </p>
        )}
        {!wantDelete ? (
          <>
            <Button variant="primary" onClick={() => void syncFirst()}>
              {tr('Synka först', 'Sync first')}
            </Button>
            <Button onClick={() => void doLogout(false)}>{tr('Spara ändringarna lokalt och logga ut', 'Keep the changes locally and sign out')}</Button>
            <p className="text-xs text-muted">{tr('Ändringarna ligger kvar i kontots egen profil på den här enheten och synkas när du loggar in igen. De syns inte i gästläget.', 'The changes stay in the account’s own profile on this device and are synced when you sign in again. They are not shown in guest mode.')}</p>
            <Button variant="ghost" onClick={cancel}>
              {tr('Avbryt', 'Cancel')}
            </Button>
            <button type="button" className="min-h-11 text-left text-xs text-muted underline" onClick={() => setWantDelete(true)}>
              {tr('Radera den lokala kopian i stället…', 'Delete the local copy instead…')}
            </button>
          </>
        ) : (
          <>
            <p>{tr('Den lokala kopian av kontot raderas från den här enheten. Kontots data på servern påverkas inte och finns kvar nästa gång du loggar in.', 'The local copy of the account is deleted from this device. The account’s data on the server is not affected and is there the next time you sign in.')}</p>
            <label className="flex min-h-11 items-start gap-2">
              <input type="checkbox" className="mt-1 h-5 w-5" checked={confirmDelete} onChange={(e) => setConfirmDelete(e.target.checked)} />
              <span>
                {unsynced > 0
                  ? tr(`Jag förstår att ${unsynced} osynkade ändring${unsynced === 1 ? '' : 'ar'} försvinner från den här enheten.`, `I understand that ${unsynced} unsynced change${unsynced === 1 ? '' : 's'} will disappear from this device.`)
                  : tr('Jag vill radera den lokala kopian på den här enheten.', 'I want to delete the local copy on this device.')}
              </span>
            </label>
            <Button variant="danger" disabled={!confirmDelete} onClick={() => void doLogout(true)}>
              {tr('Radera lokal kopia och logga ut', 'Delete local copy and sign out')}
            </Button>
            <Button variant="ghost" onClick={cancel}>
              {tr('Avbryt', 'Cancel')}
            </Button>
          </>
        )}
      </div>
    </Sheet>
  );
}

/** Meddelande som sparats inför omladdning (t.ex. efter inloggning/utloggning). */
export function useAccountNotices() {
  useEffect(() => {
    const auth = getAuthStore();
    auth.onRevocationResolved = (kind) =>
      toast({ kind: kind === 'revoked' ? 'ok' : 'info', text: kind === 'revoked' ? tr('Servern har bekräftat utloggningen.', 'The server has confirmed the sign-out.') : tr('Ingen aktiv session fanns kvar att återkalla på den här enheten.', 'There was no active session left to revoke on this device.'), timeout: 8000 });
    return () => {
      auth.onRevocationResolved = null;
    };
  }, []);
}
